import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "next-i18next";
import { useSetRecoilState } from "recoil";

import { BooleanStringV2 } from "@sellernote/shared/src/api/dto/entities/common/common";
import AUTH_QUERY from "@sellernote/shared/src/api/queries/forwarding/AUTH_QUERY";
import { ShipdaCurrentLanguage } from "@sellernote/shared/src/i18n/i18nForShipda";
import { validatePhone } from "@sellernote/shared/src/utils/validation";
import useTimer from "@sellernote/features/src/common/hooks/useTimer";
import { sendSignUpCompleteGTMEvent } from "@sellernote/features/src/common/utils/gtm";
import { getReverseBooleanString } from "@sellernote/features/src/common/utils/options";
import { FORWARDING_AUTH_ATOMS } from "@sellernote/features/src/forwarding/recoil-states/auth";
import {
  validateConfirmPassword,
  validateOtp,
  validatePassword,
  validateStatusOtp,
  validateStatusPhone,
} from "@sellernote/shipda-kr/src/utils/validation";
import TextButton from "@sellernote/ui/src/sds-v2/components/button/TextButton";
import InputTextField from "@sellernote/ui/src/sds-v2/components/form/InputTextField";
import SelectButton from "@sellernote/ui/src/sds-v2/components/form/SelectButton";
import Modal from "@sellernote/ui/src/sds-v2/components/modal/Modal";

import SelectCountryCallingCode from "../../../containers/SelectCountryCallingCode";
import {
  DEFAULT_COUNTRY_CALLING_CODE_FOR_EN,
  DEFAULT_COUNTRY_CALLING_CODE_FOR_KO,
} from "../../../containers/SelectCountryCallingCode/_constants";

import CurrentTermsOfUse from "../../policy/terms_of_use/CurrentTermsOfUse";
import { ServiceGuideRequestForm } from ".";
import Styled from "./index.styles";

const initialState = {
  password: "",
  passwordConfirm: "",
  phone: "",
  optNumber: "",
  countryData: "",
};

/**
 * TODO: 전화번호 인증 관련 부분만 회원가입 페이지와 UX를 맞춰둠, 추후 PO, PD와 협의를 통해 회원가입 페이지와 UX를 맞추는 작업이 필요해보임
 */
export default function SimpleRegisterModal({
  active,
  requestGuideForm,
  handleSimpleRegisterModalClose,
}: {
  active: boolean;
  requestGuideForm: ServiceGuideRequestForm;
  handleSimpleRegisterModalClose: () => void;
}) {
  const { t } = useTranslation(["main-landing"]);

  const setLoggedIn = useSetRecoilState(FORWARDING_AUTH_ATOMS.USER_LOGGED_IN);

  const initialCountryData =
    ShipdaCurrentLanguage.currentLanguage === "ko"
      ? `${DEFAULT_COUNTRY_CALLING_CODE_FOR_KO.countryCallingCode}-${DEFAULT_COUNTRY_CALLING_CODE_FOR_KO.countryCode}`
      : `${DEFAULT_COUNTRY_CALLING_CODE_FOR_EN.countryCallingCode}-${DEFAULT_COUNTRY_CALLING_CODE_FOR_EN.countryCode}`;

  const [opensSuccessRegisterModal, setOpensSuccessRegisterModal] =
    useState(false);

  const [registerForm, setRegisterForm] = useState(() => ({
    ...initialState,
    countryData: initialCountryData,
  }));

  const [agreesTerms, setAgreesTerms] = useState<BooleanStringV2>("FALSE");
  const [opensTermsModal, setOpensTermsModal] = useState(false);

  const [errorMessageOfOTP, setErrorMessageOfOTP] =
    useState<React.ReactNode>("");
  const [errorMessageOfPhone, setErrorMessageOfPhone] =
    useState<React.ReactNode>("");

  const {
    mutate: registerUser,
    data: registerUserData,
    ResponseHandler: ResponseHandlerOfRegisterUser,
  } = AUTH_QUERY.useRegisterUser({
    Trans,
    onSuccess: (res) => {
      sendSignUpCompleteGTMEvent({
        createdAt: res.user?.createdAt,
        userId: res.user?.id,
      });

      setOpensSuccessRegisterModal(true);

      handleSimpleRegisterModalClose();
      resetRegisterForm();
    },
    onError: () => {
      resetRegisterForm();
    },
  });

  const handleTimerFinish = () => {
    setErrorMessageOfOTP(
      <Trans i18nKey="components:landing.인증시간이_만료되었습니다._인증번호를_재전송하세요." />
    );
  };

  const { stopTimer, startTimer, minutes, seconds } = useTimer({
    countDownSeconds: 180,
    onFinish: handleTimerFinish,
  });

  const {
    mutate: generateOTP,
    data: generateOTPData,
    status: generateOTPStatus,
    reset: resetGenerateOTP,
  } = AUTH_QUERY.useGenerateOTP({
    onSuccess: () => {
      startTimer();

      setErrorMessageOfPhone("");
    },

    onError: (error) => {
      if (!error) return;

      const errorMessage = validateStatusPhone(error?.code).message;

      setErrorMessageOfPhone(errorMessage);

      stopTimer();
    },
  });

  const {
    mutate: confirmOTP,
    status: confirmOTPStatus,
    reset: resetConfirmOTP,
  } = AUTH_QUERY.useConfirmOTP({
    onSuccess: () => {
      stopTimer();
      setErrorMessageOfOTP("");
    },
    onError: (error) => {
      if (!error) return;

      const errorMessage = validateStatusOtp(error?.code).message;

      setErrorMessageOfOTP(errorMessage);
    },
  });

  const createRegisterFormChangeHandler = (key: string) => (value: string) => {
    if (key === "phone") {
      setErrorMessageOfPhone(
        validatePhone({
          val: value,
          allowEmpty: false,
          Trans,
        }).message
      );
    }

    if (key === "optNumber") {
      setErrorMessageOfOTP(validateOtp(value).message);
    }

    setRegisterForm({ ...registerForm, [key]: value });
  };

  const handleOTPGenerate = () => {
    if (errorMessageOfOTP) {
      setErrorMessageOfOTP("");
    }

    generateOTP({
      division: "phone",
      type: "register",
      value: registerForm.phone,
      countryCallingCode: registerForm.countryData.split("-")[0],
    });

    resetConfirmOTP();

    setRegisterForm({ ...registerForm, optNumber: "" });
  };

  const handleOTPConfirm = () => {
    confirmOTP({
      codeToken: generateOTPData?.codeToken,
      code: registerForm.optNumber,
    });
  };

  const handleRegisterSubmit = () => {
    const { name, email, company } = requestGuideForm;
    const { password } = registerForm;

    registerUser({
      name,
      email,
      company,
      password,
      phone: registerForm.phone,
      accountId: email,
      type: "consignee",
      advertisePermission: true,
      businessType: "person",
      countryCode: registerForm.countryData.split("-")[1],
    });
  };

  const canGenerateOTP =
    Boolean(registerForm.phone) &&
    validatePhone({
      val: registerForm.phone,
      allowEmpty: false,
      Trans,
    }).result;

  const canConfirmOTP =
    generateOTPStatus === "success" && confirmOTPStatus !== "success";

  const validatesForm = useMemo(() => {
    const { password, passwordConfirm } = registerForm;

    const isCompletedForm = password && passwordConfirm && registerForm.phone;

    const isValidPassword =
      validatePassword(password)?.result &&
      validateConfirmPassword(password, passwordConfirm)?.result;

    const isValidPhone = validatePhone({
      val: registerForm.phone,
      allowEmpty: true,
      Trans,
    }).result;

    const isValidOTP = confirmOTPStatus === "success";

    const isCheckedAgreesTerms = agreesTerms === "TRUE";

    return (
      isCompletedForm &&
      isValidPassword &&
      isValidPhone &&
      isValidOTP &&
      isCheckedAgreesTerms
    );
  }, [agreesTerms, confirmOTPStatus, registerForm]);

  const resetRegisterForm = () => {
    setRegisterForm({
      ...initialState,
      countryData: initialCountryData,
    });
    setAgreesTerms("FALSE");
    resetGenerateOTP();
    resetConfirmOTP();
  };

  // 서비스 가이드 신청 시 입력한 전화번호가 기본값, 간편 회원가입 시 변경한 전화번호가 있다면 변경된 값으로 가입.
  useEffect(() => {
    setRegisterForm({
      password: "",
      passwordConfirm: "",
      phone: requestGuideForm.phone,
      optNumber: "",
      countryData: initialCountryData,
    });
  }, [initialCountryData, requestGuideForm.phone]);

  const [countryCallingCodeSize, setCountryCallingCodeSize] = useState(0);
  const updateCountryCallingCodeSizeRef = useCallback(
    (node: HTMLDivElement) => {
      if (!node) {
        return;
      }

      requestAnimationFrame(() => {
        setCountryCallingCodeSize(node.getBoundingClientRect().width);
      });
    },
    []
  );

  return (
    <>
      <Modal
        uiType="formInput"
        active={active}
        title={t("main-landing:간편회원가입_모달_타이틀")}
        body={
          <Styled.registerForm>
            <InputTextField
              inputType="password"
              labelInfo={{ label: t("main-landing:간편회원가입폼_비밀번호") }}
              value={registerForm.password}
              setValue={createRegisterFormChangeHandler("password")}
              errorMessage={
                Boolean(registerForm.password) &&
                validatePassword(registerForm.password)?.message
              }
            />

            <InputTextField
              inputType="password"
              labelInfo={{
                label: t("main-landing:간편회원가입폼_비밀번호확인"),
              }}
              value={registerForm.passwordConfirm}
              setValue={createRegisterFormChangeHandler("passwordConfirm")}
              errorMessage={
                Boolean(registerForm.passwordConfirm) &&
                validateConfirmPassword(
                  registerForm.password,
                  registerForm.passwordConfirm
                )?.message
              }
            />

            <Styled.phoneContainer
              countryCallingCodeSize={countryCallingCodeSize}
            >
              <SelectCountryCallingCode
                ref={updateCountryCallingCodeSizeRef}
                countryData={registerForm.countryData}
                setCountryData={createRegisterFormChangeHandler("countryData")}
              />

              <InputTextField
                className="custom-input-text-field"
                inputType="tel"
                labelInfo={{ label: t("main-landing:간편회원가입폼_전화번호") }}
                value={registerForm.phone}
                setValue={createRegisterFormChangeHandler("phone")}
                addonButton={{
                  label:
                    generateOTPStatus !== "idle"
                      ? t("main-landing:간편회원가입폼_재전송")
                      : t("main-landing:간편회원가입폼_전송"),
                  onClick: handleOTPGenerate,
                  active: canGenerateOTP,
                  buttonType: "button",
                }}
                errorMessage={errorMessageOfPhone}
              />
            </Styled.phoneContainer>

            <InputTextField
              labelInfo={{
                label: t("main-landing:간편회원가입폼_인증번호"),
              }}
              inputType="tel"
              value={registerForm.optNumber}
              setValue={createRegisterFormChangeHandler("optNumber")}
              addonButton={{
                label:
                  generateOTPStatus === "success" &&
                  confirmOTPStatus === "success"
                    ? t("components:landing.인증완료")
                    : t("main-landing:간편회원가입폼_인증"),
                onClick: handleOTPConfirm,
                active: canConfirmOTP,
                extraLabel:
                  (minutes === "00" && seconds === "00") ||
                  !(
                    generateOTPStatus === "success" &&
                    confirmOTPStatus !== "success"
                  )
                    ? ""
                    : `${minutes}:${seconds}`,
                buttonType: "button",
              }}
              placeholder={t("main-landing:간편회원가입폼_인증번호입력안내")}
              disabled={
                (minutes === "00" && seconds === "00") ||
                generateOTPStatus !== "success" ||
                (generateOTPStatus === "success" &&
                  confirmOTPStatus === "success")
              }
              errorMessage={errorMessageOfOTP}
            />

            <SelectButton
              uiType="checkboxFilled"
              size="default"
              label={
                <>
                  {t("main-landing:간편회원가입폼_이용약관동의")}
                  {` `}
                  <TextButton
                    theme="gray"
                    fontSize={10}
                    label={t("main-landing:소개서신청폼_약관_자세히보기")}
                    handleClick={() => setOpensTermsModal(true)}
                    className="custom-checkbox-text-button"
                  />
                </>
              }
              value={agreesTerms}
              selected={agreesTerms === "TRUE"}
              onSelect={(v) => setAgreesTerms(getReverseBooleanString(v))}
            />
          </Styled.registerForm>
        }
        actionPositive={{
          label: t("main-landing:간편회원가입폼_가입완료_버튼"),
          handleClick: handleRegisterSubmit,
          disabled: !validatesForm,
        }}
        onClose={() => {
          resetRegisterForm();
          handleSimpleRegisterModalClose();
        }}
      />

      {ResponseHandlerOfRegisterUser}

      <Modal
        uiType="onlyDescription"
        active={opensSuccessRegisterModal}
        desc={t("main-landing:간편회원가입폼_가입완료_성공_모달")}
        actionPositive={{
          label: t("main-landing:간편회원가입폼_가입완료_성공_버튼"),
          handleClick: () => {
            if (!registerUserData) return;

            const { accessToken, refreshToken } = registerUserData;

            window.sessionStorage.setItem("accessToken", accessToken);
            window.sessionStorage.setItem("refreshToken", refreshToken);

            setLoggedIn(true);

            setOpensSuccessRegisterModal(false);
          },
        }}
      />

      <Modal
        uiType="formInput"
        active={opensTermsModal}
        title={
          <Styled.termsTitle>
            {t("main-landing:간편회원가입폼_이용약관동의_모달_타이틀")}{" "}
            <span className="required">
              {t("main-landing:소개서신청폼_약관_모달_타이틀_필수")}
            </span>
          </Styled.termsTitle>
        }
        body={
          <Styled.termsContainer>
            <CurrentTermsOfUse
              layoutSize="default"
              language={ShipdaCurrentLanguage.currentLanguage}
            />
          </Styled.termsContainer>
        }
        onClose={() => {
          setOpensTermsModal(false);
        }}
        width="960px"
      />
    </>
  );
}
